// The Briefing: the strip across the top of the SmartBox that says what you are looking at
// (frame 40006859:278034).
//
// There is one briefing on screen at a time and several things it could be about, so the question
// "which briefing, and what does it say?" needs ONE answer rather than one per component. That is
// this module. Before it, the same resolution lived inline in ContextPanel's `stage` computed and
// the search strip's own computed in SmartBox — two places that could disagree.
//
// Briefings come from two different places, and the split is deliberate:
//
//   DERIVED  — worked out from state the client already holds. The web search briefing counts the
//              results in `research`; the image briefing reads the opened item plus what the
//              browser measured. No request, so it is never stale and never loading.
//
//   FETCHED  — only the server can write it. The Email Briefing's summary sentence is composed by
//              busy-ai-srv from the sender groups, so it has a real lifecycle: idle → loading →
//              ready/error, cached against the input it was written about.
//
// Adding a briefing is therefore: write a pure builder in lib/briefings/, and add one line to
// `active` below. Nothing in the strip changes.

import { searchBriefing, searchBriefingLeads, briefedSearchTurn, afterSaveSubject } from '@/lib/briefings/search'
// "Is the search still what the screen is about?" — one predicate, so the strip and the middle
// cannot disagree about whose session it is (busy-my-web#532).
import { researchLive } from '@/lib/researchNav'
import { savedPacksForCurrentTurn } from '@/lib/researchSavedPacks'
import { selectionBriefing } from '@/lib/briefings/selection'
import { storyBriefing, countStory } from '@/lib/briefings/story'
import { CONVERSATIONS_FILTER } from '@/lib/ongoingConversations'
import { storyPacks } from '@/lib/storySteps'
// The sender groups are paged, with the TRUE number of them (Saffet 2026-10-28: "50 email groups").
import { LIST_PAGE_SIZE, appendPage, hasMore, readGroupsPage } from '@/lib/emailListPaging'
import { packBriefing } from '@/lib/briefings/pack'
import { comparisonBriefing } from '@/lib/briefings/comparison'
import { imageBriefing, isImagePreview } from '@/lib/briefings/image'
// …and WHEN that image briefing is allowed to take the strip (busy-my-web#392).
import { preferBriefing } from '@/lib/briefings/imagePriority'
import { emailBriefing, emailBriefingRequest, emailBriefingKey } from '@/lib/briefings/email'
import { graphMatches } from '@/lib/graphConnections'
// …and the card a mail FOLDER gets (busy-my-web#319): the same card, about Important / Draft /
// Trash / Spam / Scheduled rather than about the whole mailbox.
import { emailFolderBriefing } from '@/lib/briefings/emailFolder'
import { emailThreadBriefing } from '@/lib/briefings/emailThread'
import { emailConnectBriefing } from '@/lib/briefings/emailConnect'
// Home's opened Ongoing list (busy-my-web#630): the card the box wears over it, and the rule for
// what an opened list may hold.
import { ongoingListBriefing, ongoingListOf } from '@/lib/homeOngoingList'
// How many possible-Ongoing matches the card's own row states (busy-my-web#316) — the SAME reading
// of `ongoing/suggestions` the offers list under it is built from, so the count and the list can
// never disagree about how many questions there are.
// …and, since busy-my-web#612, the same grouping narrowed to the open email (`threadMatchCount`) or
// to whichever scope the open workspace has (`scopedMatchCards`).
import { ongoingConnectionCards, scopedMatchCards, threadMatchCount } from '@/lib/possibleMatches'
// …and, inside an Ongoing, the table's own reading of those cards — what the "N Possible
// connections" row counts and what its per-kind rows list (Pouria, 2026-10-01, frame
// 40011046:161537).
import { connectionKinds, connectionRows } from '@/lib/possibleConnections'
import { isBriefedMenu } from '@/lib/briefings/entityFacts'
import { scopeOf as attachmentsScope } from './attachments'
import { targetEmailCount } from '@/lib/emailSession'
import { leavesContext } from '@/lib/workboardContext'
import { forgetJourneyBelow, nextJourney } from '@/lib/briefings/trail'
import api from '@/lib/services/general.service'

/**
 * What the email strip should say while an ongoing is being made out of the picked groups
 * (busy-my-web#104): `{ groups, emails }` on the naming step and for as long as the create is in
 * flight, null otherwise. Kept here as a plain reader over the session's getters — the counts
 * themselves are the session's derived targets, so the strip cannot disagree with the panel.
 */
function createStep(rootGetters) {
  const creating = rootGetters['emailSession/createState'] === 'creating'
  if (rootGetters['emailSession/createStage'] !== 'naming' && !creating) return null
  const targets = rootGetters['emailSession/createTargets'] || []
  return { groups: targets.length, emails: targetEmailCount(targets) }
}

export const state = {
  // Fetched briefings, per kind: { status: 'idle'|'loading'|'ready'|'error', data, key }.
  // Keyed by the input it was written about, so the same input is never re-asked and a changed
  // one is never answered from the old reply.
  remote: {},

  // What the UI measured about the open image — pixel size, and bytes when the server sent a
  // length. The store cannot compute this: only a loaded <img> knows it. Cleared with the preview.
  imageMeta: null,

  // The sender groups the Email Workspace is showing, or null when it is not open. Held here
  // rather than read from the `email` module because it is the WORKSPACE's current view — the
  // filtered, archived-into set — not the mailbox, and the briefing must describe exactly what is
  // on screen. The workspace pushes it with `setEmailGroups`.
  emailGroups: null,

  // Whether the sender groups are on the wire right now (busy-my-web#339). Set SYNCHRONOUSLY by the
  // action that opens the inbox, before the request goes out, which is what lets the card be on
  // screen in the same tick as the click. It tracks the groups call and nothing else: the AI
  // sentence's own `remote.emails` record used to double as this signal, and since that fetch is a
  // language-model round trip it made "the card is coming" and "a sentence nobody draws is coming"
  // the same fact — ten seconds of skeleton for a wait the card had no reason to sit out.
  emailGroupsLoading: false,

  // How many sender groups there ARE (`POST v1/email/groups/page`'s `total`), which is what
  // "N email groups" states — not the length of the page in hand (Saffet 2026-10-28: the card said
  // "50 email groups", the page size). Null when the server did not say (an older BFF): the card
  // then falls back to the list's length, which is what it did before.
  emailGroupsTotal: null,
  // The next page of groups is on the wire (the listing's "Show more").
  emailGroupsLoadingMore: false,

  // Which Email Briefing category the workspace is showing, or null for the invitation state
  // (frame 40006958:296258). Set by clicking a category row in the strip.
  emailCategory: null,

  // The mail FOLDER page on screen, or null (busy-my-web#319): `{ folder, count }`, pushed by the
  // page itself the way the workspace pushes its groups, and withdrawn on the way out. It is what
  // turns the strip into "Briefing Important Emails" over that folder's own count. Never set on
  // the inbox: there the card IS the mailbox dashboard, which is `emailGroups`' card.
  emailFolder: null,
  // The thread the READER has open (Pouria, 2026-09-22): its card heads the box while it is on
  // screen — lib/briefings/emailThread.js. Set by components/Email/ThreadReader.vue.
  emailThread: null,

  // Whether the possible-Ongoing-matches WORKSPACE is open (busy-my-web#316, redirected by #317).
  // Not a category: it opens the questions themselves rather than a section of the mailbox, so it
  // is its own flag and leaves `emailCategory` — and everything hanging off it — alone. Held here
  // because the row that opens it is in the Briefing strip while the match cards are drawn in the
  // middle (EmailWorkspace) and the match list under the crumb in the panel — three components with
  // no relationship to each other. (Until #317 the same flag unfolded an offers list inside the
  // card; the cards replaced it, and RelationOffersBanner went with it.)
  relationsOpen: false,
  // …and WHICH email the open workspace is about (busy-my-web#612, Saffet 2026-10-09): the thread id
  // when the row pressed was an open email's "N possible ongoing matches", '' for the mailbox's own
  // row (#317). Dies with the workspace — SET_RELATIONS_OPEN(false) clears it — so the next open
  // from the inbox card is the mailbox's again.
  relationsThread: '',

  // …and the same question asked from INSIDE an Ongoing (busy-my-web#333): whether the story
  // briefing's "N possible relations" row has unfolded its cards. Its own flag, not the mailbox's:
  // the two rows live on two different briefings and are open at different moments, and sharing one
  // boolean would have opening a story's relations also arm the workspace's middle (and vice versa).
  // Reset whenever the briefed story changes — see SET_STORY.
  storyRelationsOpen: false,
  // …and WHICH module the open table is narrowed to, by a per-kind row's "View ↗" (Pouria,
  // 2026-10-01, frame 40011046:161537) — '' for all of them. Dies with the table: closing the row,
  // or briefing another story, clears it (see SET_STORY_RELATIONS_OPEN / SET_STORY).
  storyRelationsModule: '',

  // THE BREADCRUMB'S JOURNEY (busy-my-web#606, 2026-10-09 — Saffet: "The Breadcrumbs is there to
  // chart your journey"): the deepest path walked on this page, as lib/briefings/trail.js's
  // segments, and the SCOPE it was walked in (the page's path, or 'emails' for the whole email
  // workspace session). A different scope starts a fresh journey; a crumb click keeps it; a Close
  // forgets below the level it returns to. Fed by SmartBox, which is where the live trail is built.
  journey: { scope: '', segs: [] },

  // THE ONGOING LIST OPENED FROM HOME (busy-my-web#630, Pouria 2026-10-14): `{ section, label,
  // status, ongoings: [{ id, title }] }` — the count row that was clicked ("13 ongoings need your
  // attention") and the stories it counted — or null. The same kind of "opened list" `emailGroups`
  // is for "Emails need attention": the Follow-ups strip steps aside while it is up, the middle of
  // Home lists the stories, and the box is headed by its Briefing. Held here because those are three
  // components with no relationship to each other (FollowUps.vue, views/Home.vue, the SmartBox).
  ongoingList: null,

  // The ongoing story being viewed, and what it is made of. Pushed by the view that knows — the
  // counts come from several getters (outstanding items, the story's timeline, its children), and
  // re-deriving them here would duplicate that arithmetic in a second place.
  story: null,
  storyCounts: {},
  storyChildren: [],

  // The Research Pack currently open (?pack=…), and what it holds. Pushed by the view that knows,
  // for the same reason the story's counts are.
  pack: null,
  packCounts: {},

  // The comparison open in the middle (?compare=…): `{ title, packs }` (#77). Pushed by the view
  // rather than read straight off the comparison store, for the reason #72 gave: a comparison
  // survives navigation, so "there is one in the store" is not the same question as "the page is
  // showing one" — and the strip must only brief what is on screen.
  comparison: null,

  // …and whether that pack LEADS the briefing, ahead of the search sitting behind it. Off while a
  // pack is merely open next to a search (the search is the newer thing, so it briefs); ON the
  // moment a pack has just been SAVED into (Pouria, 2026-08-17: after saving, the box should brief
  // the ongoing's pack "but we keep serch result"). The results stay exactly where they are — what
  // changes is which of the two the strip is about.
  packLead: false,

  // The entity currently OPEN in the box, and the record behind it:
  // `{ menu, id, eventId, ongoingId, entity }` (frame 40007893:378129) — a task, a note or an
  // event. Three panes need it at once — the strip draws its facts grid, the pack document marks
  // where it was raised, and the Work-board lists its attachments — so it is one fact in the store
  // rather than three components each watching the same window event.
  openEntity: null,

  // The kind the user closed. Reset whenever the subject changes, so dismissing is "not this
  // one", never "not ever".
  dismissed: null,

  // WHERE the story/pack above was declared — a work-board context (lib/workboardContext.js), the
  // same string the board is scoped by (busy-my-web#84). The briefing needs its own copy of that
  // scope because a subject does not only arrive by NAVIGATING to it: saving research files it into
  // a pack and briefs that pack on the spot (`briefSavedPack`), on home, with no `?pack=` in the
  // URL. ContextPanel clears the pack by WATCHING `?pack=`, so a pack that was never in the URL had
  // nothing to clear it — it followed the user to home and to every module page until a full reload
  // (busy-my-web#122), and its "Story" title action then flipped `ongoing/packView` for a pack view
  // that is not on screen, which is a click on "Story" that changes nothing (busy-my-web#123).
  subjectOwner: null,
}

/**
 * What the briefed story is made of — as pushed by the view that worked it out, or derived from
 * the story itself when nothing was pushed (busy-my-web#156).
 *
 * `countStory` is pure and cheap, so the fallback costs nothing and closes the one way the strip
 * and the timeline could disagree: they now read the same story through the same rule.
 */
function storyCounts(s, rootGetters = null) {
  const pushed = s.storyCounts || {}
  if (Object.keys(pushed).length) return pushed
  return countStory(s.story, {
    subs: (s.storyChildren || []).length,
    getTimeline: rootGetters ? (id) => rootGetters['timeline/getTimelineById'](id) : null,
  })
}

/**
 * The attachment counts for a scope, from the attachments module — or null when that module is not
 * in the store at all.
 *
 * Defensive on purpose, like every other cross-module read in this file: `active` is a getter that
 * several tests and one or two callers exercise with a partial `rootGetters`, and a briefing must
 * degrade to "says nothing about attachments" rather than throw and take the WHOLE strip down with
 * it. `attachmentsRow(null)` is null, so a missing module simply means no row.
 */
function attachmentCounts(rootGetters, scope) {
  const read = rootGetters?.['attachments/countsData']
  return typeof read === 'function' ? read(scope) : null
}

/**
 * …and whether that fetch is out right now (busy-my-web#354) — what turns the attachments row into
 * a placeholder instead of leaving it out until the answer lands.
 *
 * Only 'loading' counts. An IDLE scope is one nobody has asked about yet, and promising a count
 * nobody is fetching would leave a shimmer on the card for as long as it is open; 'error' has been
 * answered, badly, and the row is absent exactly as it was before. Defensive on the getter for the
 * reason `attachmentCounts` above is: a partial `rootGetters` must cost the card its shimmer, never
 * the whole strip.
 */
function attachmentsPending(rootGetters, scope) {
  const read = rootGetters?.['attachments/counts']
  return typeof read === 'function' ? read(scope)?.status === 'loading' : false
}

/**
 * One page of sender groups: `POST v1/email/groups/page` (groups + the true total), and — only when
 * that route is not there yet (web deployed ahead of the BFF) — the bare list the card read before,
 * with no total. Two clusters of 2+ emails is a group, as it always was.
 */
async function fetchGroupsPage(skip) {
  const body = { minCount: 2, skip, take: LIST_PAGE_SIZE }
  try {
    const res = await api.insert('email/groups/page', body)
    return readGroupsPage(res?.data?.data ?? res?.data)
  } catch (e) {
    if (skip > 0) throw e
    const res = await api.insert('email/groups', { minCount: 2, take: LIST_PAGE_SIZE })
    return readGroupsPage(res?.data?.data ?? res?.data ?? [])
  }
}

export const getters = {
  remote: s => kind => s.remote[kind] || { status: 'idle', data: null, key: null },
  imageMeta: s => s.imageMeta,
  emailGroups: s => s.emailGroups,
  // "Are the groups still coming?" (busy-my-web#339) — the one honest loading signal for this
  // card and for the workspace's own placeholder.
  emailGroupsLoading: s => !!s.emailGroupsLoading,
  emailGroupsTotal: s => s.emailGroupsTotal,
  emailGroupsLoadingMore: s => !!s.emailGroupsLoadingMore,
  // More groups behind the ones listed — only where the server said how many there are.
  emailGroupsHasMore: s => hasMore(s.emailGroups || [], s.emailGroupsTotal),
  emailCategory: s => s.emailCategory,
  emailFolder: s => s.emailFolder,
  emailThread: s => s.emailThread,
  relationsOpen: s => !!s.relationsOpen,
  relationsThread: s => (s.relationsOpen ? s.relationsThread || '' : ''),
  storyRelationsOpen: s => !!s.storyRelationsOpen,
  storyRelationsModule: s => (s.storyRelationsOpen ? s.storyRelationsModule || '' : ''),
  // Home's opened Ongoing list (busy-my-web#630), and the yes/no every gate reads.
  ongoingList: s => s.ongoingList || null,
  ongoingListOpen: s => !!s.ongoingList,
  journey: s => s.journey?.segs || [],
  journeyScope: s => s.journey?.scope || '',

  // The subject the story pane declared, readable by anyone who has to describe what is open —
  // the task facts grid names the ongoing it is In and the pack it is Related to.
  story: s => s.story,
  storyChildren: s => s.storyChildren,
  pack: s => s.pack,
  /** The context that owns the story/pack on the strip right now (null while nothing is briefed). */
  subjectOwner: s => s.subjectOwner || null,
  /** That pack's own research items — what a task raised inside it could have come FROM. */
  packItems: s => (s.pack ? (s.story?.research || []).filter(r => r && String(r.packId || '') === String(s.pack.id)) : []),

  /** The open entity, but only while it is a TASK — for the surfaces that are about tasks alone. */
  openTask: s => (s.openEntity && s.openEntity.menu === 'tasks' ? s.openEntity : null),

  /**
   * The open entity whenever it is one the Briefing describes — a task, a note or an event
   * (busy-my-web#73). The grid, the Work-board's attachment chips and the document's callout all
   * read THIS one: the briefing is the same shell for all three kinds, so a getter that answered
   * for tasks alone would have made two of them silently kindless.
   *
   * Contacts answer here too since busy-my-web#240 — NOT for the facts grid (entityFacts still
   * declines anything outside BRIEFED_MENUS; a contact's briefing is #160's own field rows) but
   * for the Work-board's attachment chips and the box's seed watcher, which need the resolved
   * record (and its timelineId) exactly as they do for the other kinds.
   */
  openItem: s => (s.openEntity && (isBriefedMenu(s.openEntity.menu) || s.openEntity.menu === 'contacts') ? s.openEntity : null),

  /**
   * The briefing to show right now, or null.
   *
   * Order is by immediacy, not importance: the thing the user just OPENED wins over the thing
   * they were looking at — the inbox they navigated into, then the search sitting underneath it.
   *
   * With ONE exception, which is busy-my-web#392: an opened picture does not displace a PLACE.
   * It used to be read first of all, so while a photo was open the strip described the photo and
   * the story's / pack's / mailbox's breadcrumbs went with it — no way back. The picture is now
   * offered last (`preferBriefing`), so it still briefs a search or a bare home, and never takes
   * the strip off the place the user is standing in.
   */
  active: (s, g, rootState, rootGetters) => {
    // An opened picture is described by its own briefing (lib/briefings/image.js) — but it no
    // longer barges to the FRONT of the queue (busy-my-web#392). It is built here and offered to
    // `preferBriefing` at the bottom, which hands the strip back to the place the user is standing
    // in (a story, a pack, the mailbox) so the briefing AND its breadcrumbs survive an open photo.
    const preview = rootGetters['workboard/preview']
    const image = isImagePreview(preview) ? imageBriefing(preview, s.imageMeta) : null

      // An OPEN EMAIL is the subject over everything — the session's own card included, since the reader draws inside the workspace on Home and over the chat (Pouria, 2026-09-22, the "open email"
      // frames): "Briefing . <subject>" with what the thread carries and its own verbs.
      if (s.emailThread?.id) {
        const getThread = rootGetters['timeline/getTimelineById']
        const timeline = typeof getThread === 'function' ? getThread(s.emailThread.id) : null
        const suggestions = rootGetters['ongoing/suggestions'] || []
        // CARDS, not raw edges (busy-my-web#612): the row used to count every Suggested edge on the
        // thread while the workspace it opens draws grouped cards, so the two could disagree.
        const matches = threadMatchCount(suggestions, s.emailThread.id)
        const threadCard = emailThreadBriefing(timeline, {
          me: rootGetters['user/user']?.email || '',
          matches,
          conversation: !!rootGetters['emailConversation/active'],
        })
        if (threadCard) return threadCard
      }

    const subject = (() => {
      // Home's opened Ongoing list (busy-my-web#630): the stories one count row stood for, listed in
      // the middle — "Briefing · 13 ongoings need your attention" over how many there are and how
      // they split across the ladder. First of the page subjects because it is only ever opened ON
      // Home, by name, and everything that would take Home elsewhere (the email card, a
      // conversation, a search, a page change) closes it. Read off `ongoing/items` defensively, as
      // every cross-module read here is: a partial `rootGetters` costs the card its rungs, not the
      // card.
      if (s.ongoingList) {
        const card = ongoingListBriefing(s.ongoingList, rootGetters?.['ongoing/items'] || [])
        if (card) return card
      }

      // The open email group (busy-my-web#99) renames the strip after it — "Briefing · LinkedIn" —
      // the same way an open pack or entity does, so the box always says WHICH inbox it is reading.
      if (s.emailGroups) {
        return emailBriefing(
          s.emailGroups,
          s.remote.emails,
          s.emailCategory,
          rootGetters['emailSession/openGroup'] || null,
          // …and while an ongoing is being named or created out of the picked groups (#104) the
          // strip is about THAT — "Briefing create ongoing", the groups and what they will link.
          createStep(rootGetters),
          // …and the dashboard's own counts (busy-my-web#273), which are the session's: one call
          // answers all eight entries, and the strip states whatever has landed. Null until it has,
          // which is when the strip keeps the two rows it can work out on its own.
          rootGetters['emailSession/briefingCounts'],
          // …and how many attachments the mailbox holds (busy-my-web#277). One of the frame's own
          // categories that finally HAS a source: the counts come from the server (only it knows
          // what each file is), and with none fetched the row is simply absent, exactly as the
          // categories with no source still are. Its own argument, because the two counts are two
          // answers from two services and the strip draws both.
          attachmentCounts(rootGetters, 'home'),
          // …and the possible-Ongoing matches (busy-my-web#316): the offers the card used to
          // announce in a full-width banner, now one of its own stat rows. The count is how many
          // CARDS the workspace would draw (busy-my-web#317) rather than how many edges are open —
          // the row promises a number of questions and the middle must ask exactly that many, so
          // several offers grouped into one card count once. `open` says the cards are up, which is
          // what takes the rows off the card and puts the depth in the crumb instead.
          // …unless the KNOWLEDGE GRAPH has suggestions of its own (busy-my-web#710): then the row
          // is "N Possible connections" over the graph's user-wide list, and opening it draws that
          // list (lib/graphConnections `graphMatches`). An old BFF (404) or an empty answer leaves
          // the matches row exactly as it was.
          graphMatches(rootGetters, !!s.relationsOpen, scopedMatchCards(rootGetters['ongoing/suggestions'] || [], '').length) || {
            // …narrowed to the open email's own cards while the workspace was opened from one
            // (busy-my-web#612) — the crumb repeats this number, so it must be the middle's.
            count: scopedMatchCards(rootGetters['ongoing/suggestions'] || [], s.relationsOpen ? s.relationsThread : '').length,
            open: !!s.relationsOpen,
          },
          // …and whether the groups are still on the wire (busy-my-web#339), which is the ONLY
          // thing allowed to put the skeleton up — and only while the card has nothing true to
          // say. Everything already known is drawn immediately and the rest fills in under it.
          s.emailGroupsLoading,
          // …and whether the ATTACHMENT counts are still coming (busy-my-web#354), which is what
          // holds that row's slot open instead of letting the number arrive into a card that has
          // already settled. A third clock, read from the module that owns the fetch.
          attachmentsPending(rootGetters, 'home'),
        )
      }

      // …but a mailbox NOBODY HAS CONNECTED YET is briefed by the connect card instead (Fatemeh's
      // connect-email design, busy-my-web#451): with no Google account there are no counts to
      // state, and the page underneath is asking for the connection. Only once the integration
      // status is actually KNOWN — an unknown status must not flash this over a real mailbox,
      // the same rule the page's own gate follows.
      if (s.emailFolder
        && rootGetters['integrations/loaded']
        && !rootGetters['integrations/googleConnected']) {
        return emailConnectBriefing()
      }

      // A mail FOLDER page (busy-my-web#319) is briefed the same way, one level down: "Briefing
      // Important Emails" over that folder's own count, or over its "nothing here" line. It is
      // read AFTER the mailbox's own card, because a workspace that has declared its groups is
      // describing the whole inbox and that is the more immediate subject.
      if (s.emailFolder) {
        const folderCard = emailFolderBriefing(s.emailFolder.folder, {
          count: s.emailFolder.count,
          // The inbox's card is the dashboard, so it is made of the same counts the workspace's
          // card is made of — one call, one answer, two places that state it.
          counts: rootGetters['emailSession/briefingCounts'],
        })
        if (folderCard) return folderCard
      }

      // A comparison on screen is the subject over everything that led to it — the packs that were
      // ticked, the search behind them, the story they belong to (#77, frame 40006824:265349).
      if (s.comparison) {
        const built = comparisonBriefing(s.comparison.title, s.comparison.packs)
        if (built) return built
      }

      // Anything ticked and the briefing switches from what the search FOUND to what you have
      // KEPT (frame 40006905:278220) — at that point the selection is the subject.
      // …but only over RESEARCH picks. An ongoing's email connection is ticked onto the same board
      // (#105) and is not a saved photo/snippet/link: counted here it became "1 snippet" and the
      // selection briefing took the strip off the story — hiding the very panel the connection was
      // ticked for. It is the story's briefing that describes a picked connection, and it does.
      const selected = selectionBriefing(
        rootGetters['research/query'],
        (rootGetters['workboard/items'] || []).filter(i => i && i.kind !== 'email-connection'),
      )
      if (selected) return selected

      // A pack that was just saved into is the subject, ahead of the search it was saved OUT of:
      // the user's last act was filing those results, so the strip answers "what is in the pack now"
      // while the results themselves stay on screen behind it.
      // …and since Saffet's 2026-10-21 reports, a save hands the strip to the ONGOING rather than to
      // the one pack (lib/briefings/search.js `afterSaveSubject`): every pack, the possible
      // connections and the conversations, the moment Save is pressed.
      const savedSubject = afterSaveSubject({ saved: s.packLead, pack: s.pack, story: s.story })
      const packLeads = !!savedSubject

      const turns = rootGetters['research/turns'] || []
      // …and, whichever of the two is leading, WHERE THE TURN ON SCREEN was filed (busy-my-web#364,
      // narrowed by the 2026-09-15 parity audit E-8 / #489). It used to list the whole session's
      // destinations under every turn; a fresh, unticked search then opened carrying the previous
      // save's receipt. Read off the turns' own `filedPackId` stamps — the same record the story
      // rail retires its "Search opened" row by — but only while the latest turn is one of them.
      // The latest turn only while it is on screen (busy-my-web#578): inside a story a CLOSED
      // session's turn is the previous search, and a new one armed over it has not run yet.
      const search = searchBriefing(
        briefedSearchTurn(turns, {
          active: !!rootGetters['research/active'],
          placeBriefed: !!s.story?.id,
        }),
        savedPacksForCurrentTurn(turns, rootGetters['research/targetOngoingId'] || null),
      )
      // …and whether it still LEADS (busy-my-web#532). A session that has been closed — by opening
      // the pack it was filed into, by the crumb back to the pack list, by walking home — keeps its
      // turns and stops being the thing on screen, and the story underneath takes the strip back
      // with its breadcrumbs. Everywhere there is no story to hand it to, this is exactly the old
      // rule. See lib/briefings/search.js for the whole of it.
      if (search && searchBriefingLeads({
        live: researchLive({
          active: !!rootGetters['research/active'],
          searchOpen: !!rootGetters['research/searchOpen'],
          webSearchOn: !!rootGetters['research/webSearchOn'],
        }),
        packLeads,
        placeBriefed: !!s.story?.id,
        // …and a closed search that belongs to an Ongoing never leads off it — home included
        // (#532, Pouria 2026-10-01).
        ongoingSession: !!rootGetters['research/targetOngoingId'],
      })) return search

      // A Research Pack is open: it is the subject, ahead of the story it belongs to — unless it is
      // only here because a save just filed into it, which the story now briefs (above).
      if (s.pack && savedSubject !== 'story') {
        return packBriefing(
          s.pack,
          s.packCounts || {},
          rootGetters['ongoing/packView'],
          (rootGetters['ongoing/storyFilters'] || {}).kind || null,
          // The story rides along: a pack's tasks and events are the STORY's timeline events
          // tagged with the pack, so listing them under "2 tasks" (step 5) needs the story the
          // pack belongs to, not the pack.
          { story: s.story, children: s.storyChildren || [] },
        )
      }

      // Inside an ongoing story with no search in front of it, the briefing describes the story —
      // and its rows are the story's filters, which is what the capsule's pills used to be.
      return storyBriefing(
        s.story,
        rootGetters['ongoing/storyFilters'] || {},
        // The pushed counts, EXCEPT when nothing was pushed (busy-my-web#156). `briefSavedPack`
        // hands the story over with `counts: {}` — the view that owns the arithmetic (ContextPanel)
        // is not on screen when a pack is saved from home — and every row is then skipped for
        // having a count of 0, so the strip went blank over a story with three packs in hand.
        // A briefing must never describe a story as empty while holding the story: with no counts
        // to read, derive them from the story itself. The push stays the fast path (a real all-zero
        // set has keys and is kept as pushed); this is the floor under it.
        storyCounts(s, rootGetters),
        {
          // (No `canAddSub` any more — busy-my-web#220. It gated a "New sub-ongoing" row the strip
          // no longer has: the Briefing reports what a story is MADE OF, and creating a child is a
          // suggestion the box offers, not a line in the report. Who may spawn one is still asked,
          // by the panel that offers it — lib/subOngoing.js's `subOngoingOffered`.)
          // The packs themselves, which the strip lists once the packs filter is on (#56).
          packs: storyPacks(s.story),
          // …and the sub-ongoings, so the active kind's item list can include them too.
          children: s.storyChildren || [],
          // …and what has been ticked, which the strip names under its rows (step 3). Read from
          // the comparison store rather than kept as a second copy: the Work-board chips and the
          // compare ask read the same picks.
          picks: rootGetters['comparison/picks'] || [],
          // …and the sender groups it is connected to (#104), which the server sends with the story.
          connections: s.story?.emailConnections || [],
          // …and whether their row is the filter in force (#105): the chip is closeable, the middle
          // lists them, and the strip's other rows step aside the way they do for every filter.
          connectionsOpen: !!rootGetters['storyConnections/listOpen'],
          // …and whether this story auto-connects new threads from them (busy-timeline-srv#21),
          // which decides WHICH promise the "future emails" row makes: linked, or suggested.
          autoConnect: !!rootGetters['storyConnections/autoConnect'],
          // …and whether a removal receipt is still waiting to be read, which keeps the strip alive
          // when removing the last connection leaves the story with no rows at all.
          notice: !!rootGetters['storyConnections/hasNotice'],
          // …and the reply draft sitting in the composer, if there is one (busy-ai-srv#13): the
          // thread it answers and how many sources it used. Read from the ai module rather than
          // kept as a second copy — the pill over the composer and the Work-board's Sources rows
          // read the same draft, so the three cannot disagree about what it was written from.
          draft: rootGetters['ai/draftSummary'] || null,
          // …and WHICH thread that draft is being written on, when the Brain chose it rather than
          // being pointed at one (busy-main-bff#25): the choice, its reason, and the threads it
          // could have been instead, so the strip can offer the switch.
          replyPick: rootGetters['ai/draftPick'] || null,
          // …and the attachments on this story's threads (busy-my-web#277), counted by the server
          // for the story's own scope. Not derived from the story object like every other row's
          // count, because it cannot be: the client holds no documents, and what a file IS was
          // decided by busy-document-srv when it read the text.
          attachments: attachmentCounts(rootGetters, attachmentsScope(s.story?.id)),
          // …and its kept conversations (busy-my-web#654), counted by the server for `ongoing:<id>`
          // (store/modules/ongoingChats.js). Defensive on the getter, like the attachments.
          conversations: (() => {
            const read = rootGetters?.['ongoingChats/count']
            const linked = rootGetters?.['chatTopicsV2/linkedCount']?.(s.story?.id) || 0
            return (typeof read === 'function' && s.story?.id ? read(s.story.id) : 0) + linked
          })(),
          // …and the emails that may belong to THIS Ongoing (busy-my-web#333) — the same offers the
          // email card counts, narrowed to this story. Read off `ongoing/suggestions` (the list the
          // app already polls) rather than the story's own `offers` endpoint, so the workspace's
          // cards and the story's are one grouping of one list: answer a card here and the count up
          // there is right without a second read. Defensive on the getter for the reason every
          // cross-module read in this file is — a partial `rootGetters` must cost the strip a row,
          // not the whole briefing.
          // Every possible CONNECTION, not only mail, since Pouria's 2026-10-01 frame
          // (40011046:161537): the count and the per-kind rows are the table's own rows, so the
          // strip's "5 Possible connections" is exactly what the middle lists.
          // …and how many things the knowledge graph has CONFIRMED as connected to this story
          // (busy-my-web#710) — the "N Connections" row. Defensive on the getter, like the rest.
          graphConnected: (() => {
            const read = rootGetters?.['graphConnections/summary']
            return typeof read === 'function' && s.story?.id ? read('ongoing', s.story.id)?.connected || 0 : 0
          })(),
          matches: (() => {
            const rows = connectionRows(ongoingConnectionCards(rootGetters?.['ongoing/suggestions'] || [], s.story?.id))
            return {
              count: rows.length,
              open: !!s.storyRelationsOpen,
              kinds: connectionKinds(rows),
              module: s.storyRelationsOpen ? s.storyRelationsModule || '' : '',
            }
          })(),
        },
      )
    })()

    const built = preferBriefing(image, subject)
    if (!built) return null
    return s.dismissed === built.kind ? null : built
  },
}

export const mutations = {
  SET_REMOTE(s, { kind, ...record }) {
    s.remote = { ...s.remote, [kind]: { ...(s.remote[kind] || {}), ...record } }
  },
  SET_IMAGE_META(s, meta) { s.imageMeta = meta },
  SET_EMAIL_GROUPS(s, groups) { s.emailGroups = groups },
  SET_EMAIL_GROUPS_LOADING(s, loading) { s.emailGroupsLoading = !!loading },
  SET_EMAIL_GROUPS_TOTAL(s, total) { s.emailGroupsTotal = total == null ? null : Math.max(0, Number(total) || 0) },
  SET_EMAIL_GROUPS_LOADING_MORE(s, loading) { s.emailGroupsLoadingMore = !!loading },
  SET_EMAIL_CATEGORY(s, key) { s.emailCategory = key },
  SET_EMAIL_FOLDER(s, record) { s.emailFolder = record || null },
  SET_EMAIL_THREAD(s, record) { s.emailThread = record || null },
  SET_RELATIONS_OPEN(s, on) {
    s.relationsOpen = !!on
    if (!on) s.relationsThread = ''
  },
  SET_RELATIONS_THREAD(s, id) { s.relationsThread = String(id || '') },
  SET_STORY_RELATIONS_OPEN(s, on) {
    s.storyRelationsOpen = !!on
    if (!on) s.storyRelationsModule = ''
  },
  SET_STORY_RELATIONS_MODULE(s, module) { s.storyRelationsModule = String(module || '') },
  SET_ONGOING_LIST(s, list) { s.ongoingList = list || null },
  SET_JOURNEY(s, { scope = '', segs = [] } = {}) { s.journey = { scope: String(scope || ''), segs: segs || [] } },
  SET_STORY(s, { story, counts, children }) {
    // Changing WHICH story is briefed takes everything hanging off the old one with it
    // (busy-my-web#191): a Research Pack is a pack OF a story, its lead over the search results was
    // earned by a save into that story, and a dismissal was "not this briefing". Re-pushing the
    // SAME story (ContextPanel's watch fires on every count change) touches none of it.
    const next = story || null
    if (String(s.story?.id ?? '') !== String(next?.id ?? '')) {
      s.pack = null
      s.packCounts = {}
      s.packLead = false
      s.dismissed = null
      // …and the unfolded match cards (busy-my-web#333), which are THIS story's questions: carrying
      // them into the next story would open it on somebody else's offers, and the count row above
      // them would be a different number from the list under it.
      s.storyRelationsOpen = false
      s.storyRelationsModule = ''
    }
    s.story = next
    s.storyCounts = counts || {}
    s.storyChildren = children || []
  },
  SET_PACK(s, { pack, counts }) { s.pack = pack || null; s.packCounts = counts || {} },
  SET_COMPARISON(s, v) { s.comparison = v || null },
  SET_PACK_LEAD(s, v) { s.packLead = !!v },
  SET_DISMISSED(s, kind) { s.dismissed = kind },
  SET_OPEN_ENTITY(s, ref) { s.openEntity = ref || null },
  SET_SUBJECT_OWNER(s, ctx) { s.subjectOwner = ctx || null },
}

/**
 * Stamp the subject with the context it was just declared in — or drop the stamp when there is no
 * subject left to own. Called after every set/clear of the story or the pack: declaring a subject
 * always means "this is what is open HERE", so the newest declaration owns it, and an earlier
 * context never keeps a hold on it.
 */
function stampOwner(commit, s, rootGetters) {
  // `rootGetters?.[…]`: the board's context is the one fact this needs from outside, and reading it
  // defensively keeps every caller that hands the action a bare `{ commit }` working.
  commit('SET_SUBJECT_OWNER', (s?.story || s?.pack) ? (rootGetters?.['workboard/context'] || null) : null)
}

export const actions = {
  /** The UI reporting what it measured about the open image — see lib/briefings/image.js. */
  reportImageMeta({ commit }, meta) { commit('SET_IMAGE_META', meta || null) },

  /**
   * The box moved (busy-my-web#606): fold the live trail into the journey — see `nextJourney`. A
   * new scope (another page, or the email session starting/ending) starts from nothing. Nothing is
   * committed when nothing changed, so a watcher can call this on every recompute.
   */
  recordJourney({ commit, state: s }, { scope = '', trail = [] } = {}) {
    const sameScope = String(scope || '') === (s.journey?.scope || '')
    const segs = nextJourney(sameScope ? s.journey?.segs : [], trail)
    if (sameScope && JSON.stringify(segs) === JSON.stringify(s.journey?.segs || [])) return
    commit('SET_JOURNEY', { scope, segs })
  },
  /** A Close (the ✕ on an open thing, the gutter's Close): forget everything below `seg`. */
  forgetJourney({ commit, state: s }, seg) {
    commit('SET_JOURNEY', { scope: s.journey?.scope || '', segs: forgetJourneyBelow(s.journey?.segs, seg) })
  },
  clearJourney({ commit }) { commit('SET_JOURNEY', {}) },
  clearImageMeta({ commit }) { commit('SET_IMAGE_META', null) },

  /**
   * The Email Workspace declaring what it is showing — and withdrawing it on the way out, so the
   * strip falls back to whatever briefing sits underneath rather than describing an inbox the
   * user has left.
   */
  setEmailGroups({ commit, state: s }, groups) {
    // A list that got SHORTER is groups the user just archived or binned: the true total falls by
    // the same number, so the card's count moves with the listing rather than waiting for a reload.
    const before = (s?.emailGroups || []).length
    const after = (groups || []).length
    if (s?.emailGroupsTotal != null && groups && after < before) {
      commit('SET_EMAIL_GROUPS_TOTAL', s.emailGroupsTotal - (before - after))
    }
    commit('SET_EMAIL_GROUPS', groups || null)
  },

  /**
   * A folder page declaring which folder it is showing, and how many it holds (busy-my-web#319).
   *
   * The count is pushed rather than read from a store because each folder keeps its own (the
   * timeline counts, the draft module, the spam module, the scheduled module) and the card must
   * state the number the page is actually showing.
   */
  setEmailFolder({ commit }, record) {
    if (!record?.folder) {
      commit('SET_EMAIL_FOLDER', null)
      return
    }
    commit('SET_EMAIL_FOLDER', { folder: String(record.folder), count: Number(record.count) || 0 })
  },
  clearEmailFolder({ commit }) { commit('SET_EMAIL_FOLDER', null) },
  setEmailThread({ commit }, id) { commit('SET_EMAIL_THREAD', id ? { id: String(id) } : null) },
  clearEmailThread({ commit }) { commit('SET_EMAIL_THREAD', null) },
  clearEmailGroups({ commit }) {
    commit('SET_EMAIL_GROUPS', null)
    commit('SET_EMAIL_GROUPS_TOTAL', null)
    // Leaving the inbox ends any wait it was in: a flag left standing would have the next card
    // open straight into a skeleton for a request that is no longer coming (busy-my-web#339).
    commit('SET_EMAIL_GROUPS_LOADING', false)
    commit('SET_EMAIL_CATEGORY', null)
    // The offers belong to the inbox that was being briefed; leaving it closes the questions too.
    commit('SET_RELATIONS_OPEN', false)
  },

  /**
   * The possible-Ongoing-matches row (busy-my-web#316, redirected by #317): show the match cards in
   * the middle, or put the plain briefing back.
   *
   * The same toggle the banner's "Review" was, moved onto the row that replaced the banner — so
   * pressing the count is still one gesture to the questions and one back (the crumb's ✕ is the
   * other way back, and it comes here too). `openIt` forces it open for the caller that arrives
   * WANTING them (`?relations=1`, which home's line and the workspace's own URL both use).
   *
   * It changes no category: the cards REPLACE the middle for as long as they are up, and what the
   * user had open underneath is exactly what they come back to.
   */
  toggleRelations({ commit, state: s }, openIt = null) {
    commit('SET_RELATIONS_OPEN', openIt === null ? !s.relationsOpen : !!openIt)
  },

  /**
   * The OPEN EMAIL's "N possible ongoing matches" row (busy-my-web#612, Saffet 2026-10-09): the same
   * workspace, scoped to that thread's own cards. Until #612 this row armed the mailbox-wide
   * workspace (#317's `toggleRelations`), which is how one email's "2" opened five unrelated cards.
   * No id is the mailbox's open, exactly as `toggleRelations(true)`.
   */
  openThreadRelations({ commit }, threadId = '') {
    commit('SET_RELATIONS_THREAD', threadId)
    commit('SET_RELATIONS_OPEN', true)
  },

  /**
   * The Ongoing briefing's "N possible relations" row (busy-my-web#333): unfold this story's match
   * cards under it, or fold them away again.
   *
   * A toggle rather than a mode: since busy-my-web#539 (2026-09-23) the table takes the story's
   * MIDDLE while this is on (views/StoryView.vue reads the flag) — it used to unfold inside the
   * strip under its row — but the way back is still the same row pressed again, no route, no
   * crumb of its own to restore, and the flag dies with the briefing. That is why
   * this is deliberately not `toggleRelations`: that one arms a workspace in the email middle, and
   * doing that from inside an Ongoing would answer "show me this story's offers" by leaving it.
   */
  toggleStoryRelations({ commit, state: s }, openIt = null) {
    commit('SET_STORY_RELATIONS_OPEN', openIt === null ? !s.storyRelationsOpen : !!openIt)
  },

  /**
   * A per-kind row's "View ↗" under the open "N Possible connections" (Pouria, 2026-10-01, frame
   * 40011046:161537): narrow the middle's table to that module, or — pressed on the kind already
   * chosen — show every row again. It opens the table first if it was shut, so the row can never
   * narrow a table nobody can see.
   */
  viewStoryRelationsKind({ commit, state: s }, module) {
    const key = String(module || '')
    if (!s.storyRelationsOpen) commit('SET_STORY_RELATIONS_OPEN', true)
    commit('SET_STORY_RELATIONS_MODULE', s.storyRelationsModule === key ? '' : key)
  },

  /**
   * "Emails need attention" — bring the inbox up as the BRIEFING, without going anywhere.
   *
   * The follow-up used to route to /emails/workspace, which took the user off home to read a
   * summary. The briefing strip is where a summary belongs, so this loads what the strip needs and
   * leaves the page alone. The groups are fetched here rather than by the workspace view because
   * the workspace is no longer necessarily on screen.
   *
   * THE FIRST TICK IS THE POINT (busy-my-web#339). Saffet pressed this row and waited about ten
   * seconds with nothing on screen — long enough to doubt the link worked. Every one of those
   * seconds was spent inside this action's two awaits, and the card was gated on the second of
   * them finishing. So the order here is deliberate, and the first two lines are the fix:
   *
   *   1. DECLARE THE INBOX, synchronously. `emailGroups` is what `active` keys the email card on,
   *      so committing it before any request is what puts the card on screen in the same tick as
   *      the click. An empty list is enough — the card is the card, and its rows come from the
   *      dashboard counts, which home has usually already fetched.
   *   2. SAY THE GROUPS ARE COMING, also synchronously, so the card can draw a skeleton in the one
   *      case where it has nothing yet (a cold open, counts not back).
   *   3. Make sure the counts are actually on their way — home asks for them on mount, but this
   *      action is reachable from places that have not.
   *   4. Fetch the groups, and await ONLY those. This is the ~20ms call.
   *   5. Kick the AI sentence off WITHOUT awaiting it. In production that is a language-model
   *      round trip of several seconds, and since the 2026-09-02 clarification took the sentence
   *      off the card there is no row waiting for it — awaiting it bought the user nothing and
   *      cost them the whole wait.
   */
  async openEmails({ commit, dispatch, state: s, rootGetters }) {
    // ONE opened list on Home at a time (busy-my-web#630): the email card and an opened Ongoing list
    // both take the strip and the middle, so asking for the inbox puts the stories away.
    commit('SET_ONGOING_LIST', null)
    // (1) and (2) — before the first `await`, so nothing can come between the click and the card.
    // Re-entering keeps the groups already held rather than blanking them back to an empty card.
    commit('SET_EMAIL_GROUPS', s?.emailGroups || [])
    commit('SET_EMAIL_GROUPS_LOADING', true)
    // …and re-arm the strip. `dismissed` is "not this one", not "not ever" (see `reset`), and a
    // card the user has just ASKED for by name is the newest subject there is — without this, a
    // mailbox dismissed earlier in the session answered the click with nothing at all, which is
    // the same dead air #339 is about and permanent rather than ten seconds long.
    commit('SET_DISMISSED', null)
    // (3) The rows themselves. Not awaited either: they land in the card the moment they arrive.
    // Defensive on the getter, as every cross-module read in this file is: a caller with a partial
    // context must cost the card a fetch it may not need, never the card itself.
    // Both un-awaited fetches here swallow their own failure: a promise nobody is holding must not
    // be able to reject into the void, and neither of them has anything to tell this action anyway
    // — the counts land in the card by themselves, and the sentence lands nowhere at all.
    if (!rootGetters?.['emailSession/briefingCounts']) {
      Promise.resolve(dispatch('emailSession/loadBriefing', null, { root: true })).catch(() => {})
    }
    try {
      // (4) The one call this card genuinely waits on — the FIRST PAGE of the groups and how many
      // there are (Saffet 2026-10-28). A BFF without the page route answers the bare list instead.
      const { groups, total } = await fetchGroupsPage(0)
      commit('SET_EMAIL_GROUPS', groups)
      commit('SET_EMAIL_GROUPS_TOTAL', total)
      // (5) Off the critical path. Nothing on this card reads its answer; it is fetched only so
      // the record stays warm for the workspace that does re-ask it after a bulk archive.
      Promise.resolve(
        dispatch('ensureEmails', { groups, timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone }),
      ).catch(() => {})
    } catch {
      // The groups did not come — but the card is already up and the counts may well be in it, and
      // pulling a card the user is reading back off the screen is worse than a card missing its
      // two group rows. So the inbox stays declared and simply says less.
      commit('SET_EMAIL_GROUPS', s?.emailGroups || [])
    } finally {
      commit('SET_EMAIL_GROUPS_LOADING', false)
    }
  },

  /**
   * The next page of sender groups, appended (the group listing's "Show more"). Asked from where the
   * list in hand ends; a group already listed is not listed twice. A failure leaves the list as it
   * is — the rows on screen are still true.
   */
  async loadMoreEmailGroups({ commit, state: s }) {
    const loaded = s.emailGroups || []
    if (s.emailGroupsLoadingMore || s.emailGroupsLoading || !hasMore(loaded, s.emailGroupsTotal)) return
    commit('SET_EMAIL_GROUPS_LOADING_MORE', true)
    try {
      const before = loaded.length
      const { groups, total } = await fetchGroupsPage(before)
      commit('SET_EMAIL_GROUPS', appendPage(s.emailGroups || [], groups, g => g?.sender))
      // A page that added nothing ends the listing, whatever the total said (see emailSession's
      // loadMoreEntryThreads) — so "Show more" cannot ask the same empty page forever.
      if ((s.emailGroups || []).length === before) commit('SET_EMAIL_GROUPS_TOTAL', before)
      else if (total != null) commit('SET_EMAIL_GROUPS_TOTAL', total)
    } catch {
      // nothing: the listing keeps what it has, and "Show more" stays to be pressed again
    } finally {
      commit('SET_EMAIL_GROUPS_LOADING_MORE', false)
    }
  },

  /**
   * An Ongoing count row on Home, clicked (busy-my-web#630, Pouria 2026-10-14): open the stories it
   * counted as a LIST — the Follow-ups strip steps aside, the middle of Home lists every one of
   * them, and the box is headed by the list's Briefing. Retires the in-strip unfold (#336/#571).
   *
   * The same shape as `openEmails`: nothing is navigated, and `dismissed` is re-armed because a card
   * the user has just asked for by name is the newest subject there is. The email card is put away
   * first — one opened list at a time. A group with no openable story opens nothing (a list of none
   * is not a place to stand); the caller still sends a count of ONE straight to its story.
   */
  openOngoingList({ commit, dispatch, state: s, rootGetters }, group) {
    const list = ongoingListOf(group)
    if (!list) return
    // The email card opened from Home's other row declares the inbox without opening the session, so
    // `clearEmailGroups` is its way out — but never while the session itself is up: that is the email
    // workspace's own card, not Home's.
    if (s.emailGroups && !rootGetters?.['emailSession/active']) dispatch('clearEmailGroups')
    commit('SET_ONGOING_LIST', list)
    commit('SET_DISMISSED', null)
  },

  /**
   * …and put it away: the gutter's Close, the Home button, a conversation starting, a search, a page
   * change, the email card opening. A no-op when nothing is open, so every one of those can call it
   * without asking first.
   */
  closeOngoingList({ commit, state: s }) {
    if (s.ongoingList) commit('SET_ONGOING_LIST', null)
  },

  /** The ongoing view declaring which story is open and what it holds. */
  setStory({ commit, state: s, rootGetters }, payload) { commit('SET_STORY', payload || {}); stampOwner(commit, s, rootGetters) },
  clearStory({ commit, state: s, rootGetters }) { commit('SET_STORY', {}); stampOwner(commit, s, rootGetters) },

  /**
   * The box declaring what it has OPEN (frame 40007893:378129).
   *
   * The opener only ever knows the entity's id — the box is handed a story step, not a task — so
   * the record itself is resolved here: from the list when one is loaded, otherwise by id.
   * That is what makes Reminder and Attachments answerable at all; the timeline event carries
   * neither (see the dev notes for the BFF gaps).
   *
   * All three briefed kinds resolve the same way (busy-my-web#73), each from its own store. An
   * EVENT stays pool-only: the calendar has no read-one route, so an event the loaded calendar does
   * not hold is described by its timeline step alone — which is why the grid drops rows rather than
   * waiting for a record that is never coming.
   */
  async setOpenEntity({ commit, dispatch, state: s, rootGetters }, payload) {
    const ref = payload?.menu ? { menu: payload.menu, id: payload.id || null, eventId: payload.eventId || null, ongoingId: payload.ongoingId || null } : null
    if (!ref) { commit('SET_OPEN_ENTITY', null); return }
    // Contacts resolve here too (busy-my-web#240): not for the facts grid — `openItem`'s comment
    // says who reads a contact — but the record is what carries the timelineId the Work-board's
    // attachment chips hang off, and what seeds the box's contact rows when no list is loaded.
    const POOL = {
      tasks: () => rootGetters['task/tasks']?.items || [],
      notes: () => rootGetters['note/notes']?.items || [],
      events: () => rootGetters['event/events'] || [],
      contacts: () => rootGetters['contacts/allContactsList'] || [],
    }
    const FETCH_BY_ID = { tasks: 'task/fetchTaskById', notes: 'note/fetchNoteById', contacts: 'contacts/getContact' }
    const resolvable = !!ref.id && (isBriefedMenu(ref.menu) || ref.menu === 'contacts')
    const held = resolvable ? (POOL[ref.menu]().find?.(x => x?.id === ref.id) || null) : null
    const action = resolvable ? FETCH_BY_ID[ref.menu] : null
    // IS A READ ACTUALLY IN FLIGHT? (busy-my-web#335) The readers of this record have to tell
    // "the entity is still coming" apart from "the entity is not coming" — a held record is here
    // now, and an EVENT has no read-one route at all, so a calendar that does not hold it will
    // never produce one. Only a fetch that is really going out leaves the record `resolving`, and
    // it is cleared by the same commit that lands (or fails to land) the record. Without this the
    // entity rail could not tell a tick of latency from a permanent absence, and withholding
    // itself "until the record arrives" stranded the user when it never did (issue #335).
    const resolving = !held && !!action
    // …and a record we already HOLD lands on the first commit rather than after a round trip
    // through `entity: null`, so nothing that reads it flashes empty on the way in.
    commit('SET_OPEN_ENTITY', { ...ref, entity: held, resolving })
    if (!resolving) return
    const entity = await dispatch(action, ref.id, { root: true })
    // A newer open owns the strip by the time this lands — same guard as the box's own
    // loadEditBody, or a slow fetch would describe the entity the user has already left.
    if (s.openEntity?.id === ref.id) commit('SET_OPEN_ENTITY', { ...ref, entity: entity || null, resolving: false })
  },
  clearOpenEntity({ commit }) { commit('SET_OPEN_ENTITY', null) },

  /** …and which comparison, while one is open in the middle (#77). */
  setComparison({ commit }, payload) { commit('SET_COMPARISON', payload || null) },
  clearComparison({ commit }) { commit('SET_COMPARISON', null) },

  /** …and which Research Pack, when one is open. */
  // A pack declared OPEN (ContextPanel, `?pack=`) is the subject in its own right: whatever lead a
  // save gave the story is over the moment the pack itself is opened (Saffet 2026-10-21: "when I
  // open the research pack the Briefing doesn't have the details in it").
  setPack({ commit, state: s, rootGetters }, payload) {
    commit('SET_PACK', payload || {})
    if (payload?.pack) commit('SET_PACK_LEAD', false)
    stampOwner(commit, s, rootGetters)
  },
  clearPack({ commit, state: s, rootGetters }) { commit('SET_PACK', {}); commit('SET_PACK_LEAD', false); stampOwner(commit, s, rootGetters) },

  /**
   * Arriving somewhere: a story/pack briefed in a context the user has just LEFT goes with it
   * (busy-my-web#122). Same rule, and the same `leavesContext`, as the Work-board — the board and
   * the strip describe the same session, so they must be scoped by one rule rather than two.
   *
   * Only the SURFACE subjects are dropped. The email groups leave with their own session, an image
   * briefing leaves with the preview the board already clears, and the open entity is left alone:
   * it is opened by the box AFTER a navigation (the creation notice's View routes first, then opens
   * what it made — busy-my-web#112), so clearing it here would close the very thing that
   * navigation went to show.
   */
  enterContext({ commit, state: s }, ctx) {
    if (!leavesContext(s.subjectOwner, ctx)) return
    // Walking INTO the subject is not leaving it. A pack saved on home is briefed there, and the
    // notice's View then opens it at `/ongoing/:id?pack=…` (busy-my-web#112): that navigation
    // arrives at the very story the strip is about, so the briefing (and its lead over the search
    // results, which stay on screen) carries over and is simply re-stamped with where it now lives.
    if (s.story?.id && ctx === `ongoing:${s.story.id}`) {
      commit('SET_SUBJECT_OWNER', ctx)
      return
    }
    commit('SET_STORY', {})
    commit('SET_PACK', {})
    commit('SET_PACK_LEAD', false)
    commit('SET_SUBJECT_OWNER', null)
    // A strip dismissed in the context we are leaving must not stay dismissed in the new one.
    commit('SET_DISMISSED', null)
  },

  /**
   * Brief the pack that was just saved into, without touching the results it came from.
   *
   * The story rides along because a pack's tasks and events live on the STORY's timeline tagged
   * with the pack — the same reason the pack briefing takes it on the pack's own page. `dismissed`
   * is cleared too: a pack briefing closed earlier was "not this one", and this is a new one.
   */
  briefSavedPack({ commit, state, rootGetters }, { pack, counts, story, children } = {}) {
    // A pack the story has not caught up with yet is no reason to leave the search briefing up: the
    // story itself leads after a save (Saffet 2026-10-21, `afterSaveSubject`), so either is enough.
    if (!pack && !story) return
    // Only when this story is not already briefed: the view that owns it (ContextPanel) has worked
    // out its counts, and handing over an empty set here would blank them.
    if (story && state.story?.id !== story.id) {
      commit('SET_STORY', { story, counts: {}, children: children || [] })
    }
    if (pack) commit('SET_PACK', { pack, counts: counts || {} })
    commit('SET_DISMISSED', null)
    commit('SET_PACK_LEAD', true)
    // …and it belongs to the context it was saved in — home research, an ongoing, a module page.
    // This is the declaration ContextPanel cannot see (there is no `?pack=` behind it), so without
    // the stamp the pack had no way of leaving with the page it was made on (busy-my-web#122).
    stampOwner(commit, state, rootGetters)
  },

  /** …and it stops leading the moment a new search is asked. */
  packStopsLeading({ commit }) { commit('SET_PACK_LEAD', false) },

  /** The pack briefing's title action: Summary ⇄ Story. */
  togglePackView({ dispatch, rootGetters }) {
    const next = rootGetters['ongoing/packView'] === 'story' ? 'doc' : 'story'
    dispatch('ongoing/setPackView', next, { root: true })
  },

  /**
   * A briefing row that names a kind IS that kind's filter — click it to narrow the story, click
   * the active one to clear. Same action the capsule's pills dispatched, so the two cannot drift.
   */
  toggleFilter({ commit, dispatch, getters, state: s, rootGetters }, kind) {
    // ANY filter gesture on the email card folds the possible-Ongoing offers away (busy-my-web#316).
    // An open category leaves only its own row on the card (see SmartBox's `stripRows`), and the
    // possible-matches row is one of the ones that go — offers left open under a card with nothing
    // left to close them are a dead end. Stated ONCE, here, because the card has three branches
    // below that can put a filter on (attachments, a category, the generic row at the foot) and a
    // clear per branch is three places for the fourth one to be forgotten. Unconditional rather
    // than only-when-opening: a gesture that CLOSES a filter is returning the card to its full
    // rows, where the offers are folded anyway — they could not have been opened while one was on.
    if (getters.active?.kind === 'emails') commit('SET_RELATIONS_OPEN', false)

    // The ATTACHMENTS row (busy-my-web#277) is a filter over the user's FILES, not over a story's
    // steps or a pack's contents — so it is answered before any of those, and on whichever briefing
    // it is hanging off. Opening it fetches the list for the briefing's own scope: an ongoing briefs
    // its own attachments, home briefs the mailbox's, and the two never share an answer.
    // The CONVERSATIONS row (busy-my-web#654) lists the Ongoing's kept chats under itself, the way
    // attachments list files: a filter over sessions, not over the story's steps, so the middle's
    // timeline is untouched. Opening it reads the list again — a save from Home may have just
    // landed here.
    if (kind === CONVERSATIONS_FILTER) {
      const on = (getters.active?.rows || []).some(r => r.filter === CONVERSATIONS_FILTER && r.active)
      dispatch('ongoing/setStoryFilters', { kind: on ? null : CONVERSATIONS_FILTER }, { root: true })
      if (!on && s.story?.id) dispatch('ongoingChats/ensure', { ongoingId: s.story.id, force: true }, { root: true })
      return
    }

    if (kind === 'attachments') {
      const on = (getters.active?.rows || []).some(r => r.filter === 'attachments' && r.active)
      const scope = attachmentsScope(getters.active?.kind === 'story' ? s.story?.id : null)
      // Mark the row the way its own briefing marks rows: the email briefing by category, every
      // other by the story filter. Same gesture, and each briefing keeps reading its own state.
      if (getters.active?.kind === 'emails') commit('SET_EMAIL_CATEGORY', on ? null : 'attachments')
      else dispatch('ongoing/setStoryFilters', { kind: on ? null : 'attachments' }, { root: true })
      dispatch(on ? 'attachments/close' : 'attachments/open', { scope }, { root: true })
      return
    }

    // A Research Pack's rows do not filter a story — they choose what the MIDDLE shows. Three of
    // them open a view of the pack's own contents; the rest jump to the pack's Story narrowed to
    // that kind. This is exactly what the capsule's pills did before the briefing took them over.
    if (getters.active?.kind === 'pack') {
      // `email` opens the links view too (busy-timeline-srv#35): the pack's email cards are drawn
      // there, above the links. Same map as lib/briefings/pack.js — the row marks itself against
      // the view this sends the middle to, and the two must name the same one.
      const VIEW = { photo: 'images', snippet: 'notes', video: 'links', link: 'links', email: 'links' }
      const view = rootGetters['ongoing/packView']
      if (VIEW[kind]) {
        dispatch('ongoing/setPackView', view === VIEW[kind] ? 'doc' : VIEW[kind], { root: true })
        return
      }
      // Task/event/note/comparison rows just narrow which of the pack's own steps the Briefing
      // LISTS under the row (`briefingKindItems`, SmartBox.vue) — they used to also force the
      // middle to 'story' view, so clicking "2 tasks" while reading the pack's write-up document
      // swapped it away for the story timeline. Pouria, 2026-08-18: "when we click to 2 tasks we
      // need to keep doc open so on briefing we will have list so we can choose between them" — the
      // doc stays exactly where it was; only the Briefing strip grows the list to choose from.
      const on = (rootGetters['ongoing/storyFilters'] || {}).kind === kind
      dispatch('ongoing/setStoryFilters', { kind: on ? null : kind }, { root: true })
      return
    }

    // The Email Briefing's categories open a section of the workspace rather than filtering a
    // story; same gesture, different subject.
    if (getters.active?.kind === 'emails') {
      const next = s.emailCategory === kind ? null : kind
      commit('SET_EMAIL_CATEGORY', next)
      // …and the middle lists that entry's threads (busy-my-web#273). The session owns the fetch
      // because the rows are the session's — ticking one is a Work-board pick, exactly as ticking
      // a group is — while the Briefing goes on owning which entry is CHOSEN.
      dispatch('emailSession/openEntry', next, { root: true })
      return
    }
    // The story's "N email connections" row (busy-my-web#105) is a filter over the CONNECTIONS, not
    // over the story's steps: it opens the listing in the middle and leaves `storyFilters` alone, so
    // the timeline in the middle and on the rail does not move while a connection is being picked.
    if (kind === 'connections') {
      dispatch('storyConnections/toggleList', null, { root: true })
      return
    }

    const row = (getters.active?.rows || []).find(r => r.filter === kind)
    // A selected pack SURVIVES a kind toggle (Pouria, 2026-08-15): with a pack filtering the
    // middle, Tasks/Notes narrow the pack's steps rather than replacing the pack — so the packId
    // is carried through the change explicitly (SET_STORY_FILTERS would otherwise drop it with
    // the kind). Toggling the kind off returns to the canonical pack-selected state ('research'),
    // not to nothing.
    const packId = (rootGetters?.['ongoing/storyFilters'] || {}).packId || null
    // Closing the research row is the way OUT of the whole pack filter (Pouria, 2026-08-15: the
    // strip hides the other choices while a filter is on, and the row's X brings them back) — so
    // it clears the selected pack with it, where a kind's X only steps back to the pack.
    if (kind === 'research' && row?.active) {
      dispatch('ongoing/setStoryFilters', { kind: null, packId: null }, { root: true })
      return
    }
    dispatch('ongoing/setStoryFilters', {
      kind: row?.active ? (packId ? 'research' : null) : kind,
      ...(packId ? { packId } : {}),
    }, { root: true })
  },

  /**
   * A pack in the Briefing's pack list, clicked (#56).
   *
   * SELECTING is not opening: the Briefing stays where it is, and the middle keeps LISTING every
   * pack with the selected one marked (Pouria, 2026-08-16 — StoryView's card list reads
   * `storyFilters.packId`; a kind toggled on top still narrows to that pack's steps). Clicking
   * the selected one again clears it. The pack is ticked as a comparison pick at the same time, which is what puts
   * "Compare Research Packs" under the Briefing — the same gesture, and the same picks, as ticking
   * the pack cards in the middle column, so the two can never disagree.
   *
   * (Opening a pack — the "open" line under each entry — is navigation (?pack=…) and stays with the
   * component that has the router.)
   */
  selectPack({ dispatch, rootGetters }, pack) {
    const id = pack?.id ? String(pack.id) : null
    if (!id) return
    const current = (rootGetters['ongoing/storyFilters'] || {}).packId || null
    const next = current === id ? null : id
    dispatch('ongoing/setStoryFilters', { kind: 'research', packId: next }, { root: true })
    // Toggled only when the pick disagrees with the selection: selecting a SECOND pack must leave
    // the first ticked (that is a comparison being set up), and re-selecting an already-ticked pack
    // must not untick it behind the user's back.
    const picked = rootGetters['comparison/isPicked'](id)
    if (!!next !== picked) dispatch('comparison/toggle', { id, title: pack.title || '' }, { root: true })
  },

  dismiss({ commit, getters }) {
    const active = getters.active
    if (active) commit('SET_DISMISSED', active.kind)
  },
  /** A new subject re-arms the strip — dismissing means "not this one", not "not ever". */
  reset({ commit }) { commit('SET_DISMISSED', null) },

  /**
   * Fetch the Email Briefing for these groups, unless we already hold one written about exactly
   * them. The summary describes a particular set of groups, so archiving a sender must re-ask
   * while merely re-entering the workspace must not.
   */
  async ensureEmails({ commit, state: s }, { groups, timeZoneId, force = false } = {}) {
    const key = emailBriefingKey(groups)
    const held = s.remote.emails
    if (!force && held && held.key === key && (held.status === 'ready' || held.status === 'loading')) return

    commit('SET_REMOTE', { kind: 'emails', status: 'loading', key })
    try {
      const res = await api.insert('ai/email-briefing', emailBriefingRequest(groups, timeZoneId))
      const data = res?.data?.data ?? res?.data ?? {}
      commit('SET_REMOTE', { kind: 'emails', status: 'ready', key, data })
    } catch {
      // Not a dead end: emailBriefing() falls back to the counts we already hold, so the strip
      // still says something true.
      commit('SET_REMOTE', { kind: 'emails', status: 'error', key, data: null })
    }
  },
}
